import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { AccountingApi } from '_api';
import { MODAL_KEYS } from '_constants/modals';
import { handleError, handleResponse } from '_helpers/api';
import { hideModal } from '_store/modals/slice';

export const getAccounts = createAsyncThunk(
  'bankAccounts/getAccounts',
  async function ({ customerId, productId }, { rejectWithValue }) {
    try {
      const response = await AccountingApi.getAccounts({ customerId, productId });
      return handleResponse(response);
    } catch (error) {
      const processedError = handleError(error);
      return rejectWithValue(processedError);
    }
  }
);

export const addPlaidAccount = createAsyncThunk(
  'bankAccounts/addPlaidAccount',
  async function ({ customerId, productId, publicKey }, { dispatch, rejectWithValue }) {
    try {
      await AccountingApi.addPlaidAccount({ customerId, productId, publicKey });
      dispatch(getAccounts({ customerId, productId }));
    } catch (error) {
      const processedError = handleError(error);
      return rejectWithValue(processedError);
    }
  }
);

export const deleteAccount = createAsyncThunk(
  'bankAccounts/deleteAccount',
  async function ({ customerId, productId, accountId }, { dispatch, rejectWithValue }) {
    try {
      await AccountingApi.deleteAccount({ customerId, productId, accountId });
      dispatch(hideModal({ key: MODAL_KEYS.bankAccountDelete }));
      dispatch(getAccounts({ customerId, productId }));
    } catch (error) {
      const processedError = handleError(error);
      return rejectWithValue(processedError);
    }
  }
);

const accountsSlice = createSlice({
  name: 'bankAccounts',
  initialState: {
    data: [],
    error: null,
    loading: false,
    adding: false,
    deleting: false,
  },
  reducers: {
    resetData(state) {
      state.data = [];
    },
    resetError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAccounts.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(getAccounts.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
      })
      .addCase(getAccounts.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      });

    builder
      .addCase(addPlaidAccount.pending, (state) => {
        state.error = null;
        state.adding = true;
      })
      .addCase(addPlaidAccount.fulfilled, (state) => {
        state.adding = false;
      })
      .addCase(addPlaidAccount.rejected, (state, action) => {
        state.error = action.payload;
        state.adding = false;
      });

    builder
      .addCase(deleteAccount.pending, (state) => {
        state.error = null;
        state.deleting = true;
      })
      .addCase(deleteAccount.fulfilled, (state) => {
        state.deleting = false;
      })
      .addCase(deleteAccount.rejected, (state, action) => {
        state.error = action.payload;
        state.deleting = false;
      });
  },
});

export const { resetData, resetError } = accountsSlice.actions;

export default accountsSlice.reducer;
